import { Review } from '@kizi/comparator-core';
import { HealthCare_CompanyReview } from '@kizi/comparator-core/src/services/comparator-api/graphProduct';
import { Div, DivProps, Label, P, Span } from '@kizi/core-components';
import { IconV2 as Icon, Rating, withTooltip } from '@kizi/shared-components';
import React, { MouseEventHandler } from 'react';
import { isMobile } from '../utils/global';

export interface RatingReviewsProps extends DivProps {
  onReviewsClick?: MouseEventHandler;
  data?: Review;
}

export const RatingReviews: React.FC<RatingReviewsProps> = ({
  onReviewsClick,
  data,
  ...restProps
}: RatingReviewsProps) => {
  if (!data) return null;

  const { averageScore, totalReviewsCount } = data;

  return (
    <Div variant="ratingReviews" {...restProps}>
      <Div variant="paragraphs.captionRegularSmall">
        <Rating
          value={averageScore / 2}
          iconProps={{ name: 'star', size: 13, fill: 'main.3', mr: 1 }}
          disabled
          disabledColor={'main.3'}
        />
      </Div>
      <Label
        onClick={onReviewsClick}
        style={{
          cursor: onReviewsClick !== undefined ? 'pointer' : '',
        }}
        variant="reviews"
      >{`${totalReviewsCount} beoordelingen`}</Label>
    </Div>
  );
};

export interface HealthRatingReviewsProps extends DivProps {
  reviews?: HealthCare_CompanyReview;
  purchasableProduct?: boolean;
  comparatorName: string;
}

export const HealthRatingReviews: React.FC<HealthRatingReviewsProps> = ({
  reviews,
  purchasableProduct,
  comparatorName,
  ...restProps
}: HealthRatingReviewsProps) => {
  if (!reviews) return null;
  const { score, nrOfRatings } = reviews;
  const disabled = !purchasableProduct || nrOfRatings < 30;

  const IconWithTooltip = withTooltip<React.ComponentProps<typeof Icon>>(Icon);

  const tooltipPosition = isMobile() ? 'top-end' : 'top';

  return (
    <Div
      mb={isMobile() ? 0 : 4}
      py={1}
      px={2}
      width="fit-content"
      display="flex"
      alignItems="center"
      gridGap={1}
      borderRadius="100px"
      backgroundColor="#F0F0EF"
      {...restProps}
    >
      <Icon height={16} name="star" fill={disabled ? '#BEC0C3' : '#FFC60D'} />
      <Span
        fontWeight={1}
        variant="footnoteRegularToCaption"
        color={disabled ? '#7f7f7f' : undefined}
      >
        {score?.toFixed(1).toString().replace('.', ',')}
      </Span>
      <P
        fontSize="12px!important"
        variant="footnoteRegularToCaption"
        color={!disabled ? undefined : '#7f7f7f'}
      >{`(${nrOfRatings})`}</P>
      <IconWithTooltip
        name="info"
        size={16}
        css={{ fill: '#BEC0C3', marginRight: '0 !important' }}
        _popTo={tooltipPosition}
        _tooltip={
          <>
            {
              'Dit zijn reviews over Zorgverzekeraars die zijn achtergelaten op '
            }
            <a
              target={isMobile() ? '_self' : '_blank'}
              href="https://www.zorgkaartnederland.nl/"
              rel="noreferrer"
            >
              zorgkaartnederland.nl
            </a>
            .
            {nrOfRatings < 30
              ? comparatorName === 'cb-health' &&
                ' Let op: het aantal reviews is lager dan 30 stuks.'
              : null}
            {comparatorName === 'os-health' &&
              ' Als er minder dan 30 reviews beschikbaar zijn, worden de reviews van het hele concern getoond om een completer beeld te geven.'}
          </>
        }
        cursor="pointer"
      />
    </Div>
  );
};
